import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categories, getComicsByCategory } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const activeSlug = slug || 'all';
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  const activeCategory = categories.find((c) => c.slug === activeSlug);
  const categoryName = activeCategory ? activeCategory.name : '全部';
  const filteredComics = getComicsByCategory(activeSlug);

  const sortedComics = [...filteredComics].sort((a, b) => {
    if (sortBy === 'popular') return b.views - a.views;
    if (sortBy === 'rating') return b.rating - a.rating;
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });

  const seoStructuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: `${categoryName}漫画 - 亲亲漫画网`,
      description: `亲亲漫画网${categoryName}漫画专区，免费下拉式看${categoryName}漫画，下拉式全集在线看，畅享流畅阅读体验。`,
      url: `https://qinqinmanga.com/category/${activeSlug}`,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://qinqinmanga.com/' },
        { '@type': 'ListItem', position: 2, name: `${categoryName}漫画`, item: `https://qinqinmanga.com/category/${activeSlug}` },
      ],
    },
  ];

  return (
    <>
      <SEOHead
        title={`${categoryName}漫画 - 亲亲漫画网免费下拉式阅读`}
        description={`亲亲漫画网${categoryName}漫画专区，免费下拉式看${categoryName}漫画，下拉式全集在线看。丰富的${categoryName}漫画资源，流畅的下拉式阅读体验，随时随地畅享漫画世界。`}
        keywords={`${categoryName}漫画,下拉式漫画,${categoryName}下拉式,免费下拉式看漫画,下拉式全集,亲亲漫画网,在线看漫画,漫画大全`}
        canonicalUrl={`/category/${activeSlug}`}
        structuredData={seoStructuredData}
      />
      <ScrollToTop />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 w-full px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: `${categoryName}漫画` },
              ]}
            />

            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                {categoryName}漫画
              </h1>
              <p className="text-sm text-foreground-500 mt-2">
                探索丰富的{categoryName}漫画资源，畅享下拉式阅读体验
              </p>
            </div>

            {/* Category Tabs */}
            <div className="flex flex-wrap gap-2 mb-6">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-500 border border-background-200/70'
                  }`}
                >
                  <i className={`${cat.icon} text-base`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            {/* Sort + Count */}
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-foreground-500">
                共 <strong className="text-foreground-800">{filteredComics.length}</strong> 部作品
              </p>
              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-400">排序：</span>
                <div className="flex bg-background-100 rounded-full p-0.5">
                  {[
                    { key: 'latest', label: '最新' },
                    { key: 'popular', label: '最热' },
                    { key: 'rating', label: '评分' },
                  ].map((opt) => (
                    <button
                      key={opt.key}
                      onClick={() => setSortBy(opt.key as 'latest' | 'popular' | 'rating')}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                        sortBy === opt.key
                          ? 'bg-background-50 text-foreground-900'
                          : 'text-foreground-500 hover:text-foreground-700'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Comic Grid */}
            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5 pb-14">
                {sortedComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">暂无该分类的漫画</p>
                <Link
                  to="/category/all"
                  className="inline-block mt-4 text-primary-500 hover:text-primary-600 text-sm font-medium cursor-pointer"
                >
                  查看全部漫画
                </Link>
              </div>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}